<template>
    <div class="flex xxs:flex-col lg:flex-row">
        <div class="xxs:order-2 lg:order-1 flex flex-col xxs:w-full lg:w-3/4">
            <div
                class="relative z-[1] min-h-60 items-center flex xxs:py-4 lg:py-6 mb-6"
                :class="organization.theme_settings.cover ? 'bg-cover bg-center bg-no-repeat w-full lg:rounded' : 'bg-primary-1 lg:rounded'"
                :style="[
                    hasCover
                        ? { backgroundImage: `url('${useImagePathFormatter(organization.slug, organization.theme_settings.cover)}')` }
                        : { backgroundColor: currentColor }
            ]">
                <div class="flex xxs:flex-col lg:flex-row xxs:items-start lg:items-center  w-full bottom-1/4 px-10">
                      <div v-if="hasLogo" class="w-40 h-40 rounded-full flex items-center justify-center relative z-[0]">
                        <Image :src="useImagePathFormatter(organization.slug, logo)"
                            alt="logo"
                            class="object-contain w-40 h-40"
                            roundness="rounded-full"/>
                      </div>

                      <div v-else class="xxs:w-20 xxs:h-20 lg:w-40 lg:h-40 rounded-full flex items-center justify-center bg-black shrink-0">
                        <DefaultOrgIcon :color="defaultIconColor" />
                      </div>

                      <p class="text-body-2 md:text-body-1 font-medium lg:ml-8 xxs:mt-3 lg:mt-0 xxs:max-w-full lg:max-w-[70%] inline-block"
                          :class="[organization.name.length > 100 ? 'lg:text-display-6' : 'lg:text-display-5']"
                          :style="colorObject">
                          {{organization.name ?? 'Organization Name'}}
                      </p>
                </div>
            </div>
            <div class="xxs:order-1 lg:order-2 xxs:mt-4 lg:mt-0 flex xxs:flex-col lg:flex-row flex-grow justify-between xxs:px-4 lg:px-0">
                <div class="flex flex-row xxs:w-full lg:w-1/2">

                    <div class="w-full rounded connect-card bg-white p-5">
                        <div class="flex flex-row items-center">
                            <GroupIcon :color="currentColor" class="h-6 w-6 mr-3" />
                            <p class="text-body-5 text-default-1">
                                You can always change your brand color in your organization settings.
                            </p>
                        </div>
                        <p class="text-body-5 text-default-1 mt-10"> Here are a few component examples with the color:</p>
                        <Button buttonType="primary" label="Primary button" size="default" class="mt-5"
                                :style="{ backgroundColor: currentColor, color: contrastColor }"/>
                        <Button buttonType="primary" label="Disabled button" size="default" class="mt-5"
                                :style="{ backgroundColor: currentColor, color: contrastColor }" disabled/>
                        <Button buttonType="secondary" label="Secondary button" size="default" class="mt-5" :style="{borderColor: currentColor, color: currentColor}"/>
                        <Button buttonType="secondary" label="Disabled button" size="default" class="mt-5" :style="{borderColor: currentColor, color: currentColor}" disabled/>
                        <p class="text-body-6 mt-4" :style="{color: currentColor}">Text button</p>

                    </div>
                </div>
                <div class="flex flex-row xxs:w-full lg:w-1/2 xxs:mt-4 lg:mt-0 lg:ml-6">
                    <div class="w-full rounded connect-card bg-white p-5">
                        <div class="flex flex-row justify-between">
                            <div class="flex w-4/5">
                                <ArrowRight :color="currentColor"/>
                                <div class="w-4/5 skeleton-loader h-5 ml-4"/>
                            </div>
                            <img src="@/assets/icons/dots.svg" alt="dots"/>
                        </div>

                        <div class="flex w-full rounded-md border border-interactive-9 bg-white min-h-7 mb-5 mt-6">
                            <div class="w-1/2 flex justify-center items-center h-10 rounded-[4px] text-body-6 cursor-pointer font-bold"
                                    :style="{background: currentColor}">
                                <p :style="{color: contrastColor}" > Section 1 </p>
                            </div>
                            <div class="w-1/2 flex justify-center items-center h-10 rounded-[4px] text-body-6 text-default-1 cursor-pointer font-bold bg-white hover:text-primary-1">
                                Section 2
                            </div>
                        </div>
                        <div  v-for="i in 2" :key="i" class="flex flex-col">
                            <div class="flex flex-row justify-between mb-3">
                                <div class="w-1/4 h-4 skeleton-loader"/>
                                <div class="w-1/4 h-4 skeleton-loader"/>
                            </div>
                            <div class="w-2/3 skeleton-loader h-2.5 mt-3 mb-1.5"/>
                            <div class="3/4 skeleton-loader h-2.5"/>
                            <hr class="bg-interactive-9 h-px my-6" />
                        </div>
                        <div class="flex justify-between mt-4">
                            <Communications :color="currentColor"/>
                            <Done :color="currentColor" />
                            <BlogEmpty :color="currentColor"/>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <ColorPicker
            class="xxs:order-1 lg:order-2 xxs:w-full lg:w-1/4 xxs:ml-0 lg:ml-6 flex flex-col rounded bg-white p-6 xxs:h-auto lg:h-2/3"
            :loading="loaderPicker"
            @save="saveOrganizationColor"
            @change="changeColor"
            @close="router.push({name: 'dashboard'})"
        >
            Change organization main color
        </ColorPicker>
        <Modal v-model:show="showOrganizationSettings" @close="closeThemeSettings">
            <DashboardSettings @finished="finishDashboard" type="create" @close="closeThemeSettings"/>
        </Modal>
    </div>
</template>
<script setup>
import {computed, onMounted, ref} from "vue";
import {useAuth} from "@/stores/AuthStore";
import ArrowRight from "@/components/icons/navigation/ArrowRight.vue";
import Communications from "@/components/icons/Communications.vue";
import Button from "@/components/core/Button.vue";
import {useImagePathFormatter} from "@/composables/useImagePathFormatter";
import {useOrganizations} from "@/composables/useOrganizations";
import {toast} from "vue3-toastify";
import {useRoute, useRouter} from "vue-router";
import Modal from "@/components/core/Modal.vue";
import DashboardSettings from "@/components/modals/partials/DashboardSettings.vue";
import {useTheme} from "@/stores/ThemeStore";
import {storeToRefs} from "pinia";
import GroupIcon from "@/components/icons/Group.vue";
import {useColorContrastRatio} from "@/composables/colorContrastRatio";
import Done from "@/components/icons/email/Done.vue";
import BlogEmpty from "@/components/icons/BlogEmpty.vue";
import ColorPicker from "@/components/custom/ColorPicker.vue";
import DefaultOrgIcon from "@/components/icons/small/DefaultOrgIcon.vue";
import Image from "@/components/custom/Image.vue";

const router = useRouter();
const route = useRoute();
const {organization} = storeToRefs(useAuth());
const {setTheme} = useTheme();
const {color, textColor} = storeToRefs(useTheme());
const {updateOrganizationInfo} = useOrganizations();
const {getContrastingColor} = useColorContrastRatio();

const currentColor = ref(color.value.hex ?? '#2986CC');
const contrastColor = ref(textColor.value ?? '#FFFFFF');
const showOrganizationSettings = ref(false);
const loaderPicker = ref(false);

const changeColor = (newColor) => {
    currentColor.value = newColor;
}

const saveOrganizationColor = (color) => {
    updateOrganizationInfo(organization.value.slug,{color: color}).then((response) => {
        setTheme(response.data.theme_settings);
        toast.success('Organization color updated successfully!');
        router.push({name: 'dashboard'});
    }).catch((error) => {
        console.log(error);
        toast.error('Organization color update failed!');
    });
}

const closeThemeSettings = () => {
    showOrganizationSettings.value = false;
    router.push({name: 'dashboard'});
}

function finishDashboard() {
    showOrganizationSettings.value = false;
    router.push('theme-settings');
}

const hasLogo = computed(() => {
    return typeof organization.value.theme_settings.logo != 'undefined' && organization.value.theme_settings.logo !== null;
});

const defaultIconColor = computed(() => {
    return {hex: getContrastingColor(color.value.hex)};
})

const hasCover = computed(() => {
    return typeof organization.value.theme_settings.cover != 'undefined' && organization.value.theme_settings.cover !== null;
});

const colorObject = computed(() => {
    if(organization.value.theme_settings.banner_text_color || typeof organization.value.theme_settings.banner_text_color != 'undefined') {
        return {color: organization.value.theme_settings.banner_text_color.hex}
    }

    return {color: ''};
})

onMounted(() => {
    if(route.params.showModal) {
        showOrganizationSettings.value = true;
    }
});
</script>